import { useCallback } from 'react';

import { useAuthorizedUser } from 'Services/User/useAuthorizedUser';
import { useAudioHandlers } from 'Services/Audio/useAudioHandlers';
import { useSignInModal } from 'Shared/Auth/AuthModal/useSignInModal';
import { useCheckout } from 'Shared/hooks/useCheckout';
import { useIsDarkColorScheme } from 'Services/colorScheme/useIsDarkColorScheme';
import { trackEvent } from 'Services/Audio/audioService/utils';
import { getAudioTrackThumbnail } from 'UI/Components/AudioPlayer/utils';

import { getAudioUrl } from './getAudioUrl';

import type { FullArticleT } from 'Shared/types/ApiModels';
import type { ArticleTypeT } from 'Constants/articleTypes';
import type { ServicePlanArticleT } from 'Services/api/types';

type ParamsT = {
  readonly article: FullArticleT | ServicePlanArticleT;
  readonly articleType: ArticleTypeT;
  readonly isLocked: boolean;
  readonly isProLocked: boolean;
  readonly isPlaying: boolean;
  readonly mpServicePlanId?: string;
};

export const useHandlePlayClick = ({
  article,
  articleType,
  isLocked,
  isProLocked,
  isPlaying,
  mpServicePlanId,
}: ParamsT) => {
  const isDarkMode = useIsDarkColorScheme();
  const { isAuthorized } = useAuthorizedUser();
  const { toggle, play } = useAudioHandlers();
  const audioUrl = getAudioUrl(article.id, articleType, mpServicePlanId);
  const { openSignInModal } = useSignInModal();
  const openCheckoutModal = useCheckout({
    source: { audio_player: article.id },
  });

  return useCallback(() => {
    if (isProLocked) {
      return;
    }

    if (!isLocked) {
      const options = {
        soundtrackTitle: article.title,
        soundtrackUrl: audioUrl,
        soundtrackAuthor: article.author?.nick ?? '',
        soundtrackThumbnail: getAudioTrackThumbnail(
          articleType,
          isDarkMode,
          article,
        ),
        soundtrackPageUrl: article.links.self,
        soundtrackEntityId: article.id,
        soundtrackPublishOn: article.publishOn,
      };

      const action = isPlaying ? toggle : play;

      action(options);

      return;
    }

    trackEvent('play_click', { entityId: article.id, title: article.title });

    if (isAuthorized) {
      openCheckoutModal();

      return;
    }

    openSignInModal();
  }, [
    isProLocked,
    isLocked,
    isAuthorized,
    openSignInModal,
    article,
    audioUrl,
    articleType,
    isDarkMode,
    isPlaying,
    toggle,
    play,
    openCheckoutModal,
  ]);
};
